import { Hand, MicOff } from "lucide-react";

import { AnimalFigure } from "./AnimalFigure";
import type { StagePerson } from "./FireStage";
import { cn } from "@/engine/support/classNames";

/**
 * The app's own clearing: a night sky with sparse stars, a ring of flat
 * triangular pines in two purple shades, and a low fire lighting the floor.
 *
 * `FireStage` beside it is the camp line's clearing, drawn on the entry,
 * sign-in and connecting screens. This stage keeps its people (AnimalFigure,
 * the same StagePerson contract and seat projection) and draws the landing's
 * own scenery and fire.
 */
export const NIGHT_SEAT_COUNT = 12;

/** Order used when a person has no seat of their own: front first, then spread. */
const SEAT_FALLBACK = [0, 6, 3, 9, 1, 7, 4, 10, 2, 8, 5, 11] as const;

export interface NightStageProps {
  people: readonly StagePerson[];
  /** `fire` lights the clearing, `bubbles` draws your earshot circle instead. */
  mode?: "fire" | "bubbles" | undefined;
  className?: string | undefined;
}

interface Placed {
  person: StagePerson;
  left: string;
  top: string;
  depth: number;
  scale: number;
}

function project(person: StagePerson, x: number, y: number): Placed {
  const depth = (y + 1) / 2;
  const vy = y < 0 ? y * 0.3 : y;
  return {
    person,
    depth,
    scale: 0.74 + 0.3 * depth,
    left: `calc(50% + (var(--ring-rx) * ${x.toFixed(4)}))`,
    top: `calc(var(--ring-cy) + (var(--ring-ry) * ${vy.toFixed(4)}))`,
  };
}

const clamp = (value: number) => Math.min(1, Math.max(-1, value));

function placePeople(people: readonly StagePerson[]): Placed[] {
  const taken = new Set<number>();
  for (const person of people) {
    if (person.seat !== undefined) {
      taken.add(
        ((person.seat % NIGHT_SEAT_COUNT) + NIGHT_SEAT_COUNT) %
          NIGHT_SEAT_COUNT,
      );
    }
  }
  let cursor = 0;
  const nextFreeSeat = () => {
    while (cursor < SEAT_FALLBACK.length) {
      const seat = SEAT_FALLBACK[cursor++] ?? 0;
      if (!taken.has(seat)) {
        taken.add(seat);
        return seat;
      }
    }
    return 0;
  };

  return people.map((person) => {
    if (person.position) {
      return project(
        person,
        clamp(person.position.x),
        clamp(person.position.y),
      );
    }
    const seat =
      person.seat === undefined
        ? nextFreeSeat()
        : ((person.seat % NIGHT_SEAT_COUNT) + NIGHT_SEAT_COUNT) %
          NIGHT_SEAT_COUNT;
    const angle = (seat / NIGHT_SEAT_COUNT) * Math.PI * 2;
    return project(person, Math.sin(angle), Math.cos(angle));
  });
}

/* ---------------------------------------------------------------- scenery */

const STARS = [
  [7, 9, 1],
  [18, 16, 0.7],
  [30, 6, 1.1],
  [42, 14, 0.8],
  [55, 8, 1],
  [66, 17, 0.7],
  [76, 6, 1.2],
  [86, 14, 0.8],
  [94, 9, 1],
  [13, 24, 0.6],
  [50, 22, 0.7],
  [80, 24, 0.6],
] as const;

/** Flat triangular pines in two purple shades, layered across the top third. */
const PINES_BACK = [
  [-3, 26],
  [6, 34],
  [16, 24],
  [26, 32],
  [37, 25],
  [47, 35],
  [58, 24],
  [68, 33],
  [79, 25],
  [89, 32],
  [99, 26],
] as const;

const PINES_FRONT = [
  [1, 20],
  [12, 27],
  [23, 19],
  [34, 26],
  [45, 20],
  [56, 28],
  [66, 19],
  [77, 26],
  [88, 20],
  [97, 24],
] as const;

function NightSky() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-[38%] overflow-hidden bg-[linear-gradient(to_bottom,hsl(var(--background)),hsl(var(--panel)))]"
    >
      {STARS.map(([x, y, size], index) => (
        <span
          key={`star-${index}`}
          className="absolute rounded-full bg-foreground/80 shadow-[0_0_6px_hsl(var(--foreground)/0.5)]"
          style={{
            left: `${x}%`,
            top: `${y}%`,
            width: `${size * 3}px`,
            height: `${size * 3}px`,
          }}
        />
      ))}
    </div>
  );
}

function PineRing() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-[6%] h-[32%] overflow-hidden"
    >
      {PINES_BACK.map(([x, height], index) => (
        <span
          key={`pine-back-${index}`}
          className="absolute bottom-0 w-[9%] bg-identity-lilac/40 [clip-path:polygon(50%_0,100%_100%,0_100%)]"
          style={{ left: `${x}%`, height: `${height * 4}%` }}
        />
      ))}
      {PINES_FRONT.map(([x, height], index) => (
        <span
          key={`pine-front-${index}`}
          className="absolute bottom-0 w-[8%] bg-identity-plum [clip-path:polygon(50%_0,100%_100%,0_100%)]"
          style={{ left: `${x}%`, height: `${height * 4}%` }}
        />
      ))}
      <span className="absolute inset-x-0 bottom-0 h-8 bg-[linear-gradient(to_bottom,transparent,hsl(var(--background)))]" />
    </div>
  );
}

function Clearing({ lit }: { lit: boolean }) {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-[36%] bottom-0 bg-[linear-gradient(to_bottom,hsl(var(--panel)),hsl(var(--background)))]"
    >
      {lit ? (
        <span className="absolute inset-x-0 top-[4%] bottom-0 bg-[radial-gradient(ellipse_at_50%_44%,hsl(var(--primary)/0.4),hsl(var(--primary)/0.1)_46%,transparent_72%)]" />
      ) : null}
    </div>
  );
}

const STONES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
const SPARKS = [
  [42, 0],
  [50, 0.6],
  [58, 1.2],
  [46, 1.8],
  [55, 2.4],
] as const;

/** Stones, crossed logs, a low wide flame and sparks over the clearing floor. */
function NightFire() {
  return (
    <div
      aria-hidden="true"
      className="absolute left-1/2 h-24 w-48 -translate-x-1/2 -translate-y-[72%] sm:h-28 sm:w-64"
      style={{ top: "var(--ring-cy)" }}
    >
      <span className="absolute inset-x-4 bottom-0 h-10 rounded-[50%] bg-[radial-gradient(ellipse,hsl(var(--primary)/0.5),transparent_70%)] blur-md" />
      {STONES.map((index) => {
        const angle = (index / STONES.length) * Math.PI * 2;
        return (
          <span
            key={`stone-${index}`}
            className="absolute size-3.5 rounded-full bg-raise shadow-[inset_0_-2px_3px_hsl(var(--background)/0.7)]"
            style={{
              left: `calc(50% + ${(Math.sin(angle) * 44).toFixed(2)}px - 7px)`,
              top: `calc(76% + ${(Math.cos(angle) * 13).toFixed(2)}px - 7px)`,
            }}
          />
        );
      })}
      <span className="absolute bottom-4 left-1/2 h-2 w-24 -translate-x-1/2 -rotate-6 rounded-full bg-elevated" />
      <span className="absolute bottom-3.5 left-1/2 h-2 w-24 -translate-x-1/2 rotate-8 rounded-full bg-elevated" />
      <span className="absolute bottom-4 left-1/2 h-2 w-16 -translate-x-1/2 rounded-full bg-primary/50 blur-[2px]" />

      <svg
        viewBox="0 0 72 86"
        className="fire-flicker absolute bottom-3 left-1/2 h-16 w-12 -translate-x-1/2 overflow-visible"
        fill="none"
      >
        <path
          d="M36 4C40 19 57 25 57 46c0 17-9 29-21 29S15 63 15 47c0-13 7-23 17-33 0 10 4 14 4 14 4-8 5-15 0-24Z"
          className="fill-primary"
        />
        <path
          d="M36 34c3 8 10 12 10 22 0 8-4 14-10 14s-10-6-10-14c0-7 4-13 10-22Z"
          className="fill-you"
        />
      </svg>
      <span className="absolute bottom-6 left-1/2 size-20 -translate-x-1/2 rounded-full bg-primary/25 blur-2xl" />
      {SPARKS.map(([x, delay], index) => (
        <span
          key={`spark-${index}`}
          className="camp-spark absolute bottom-10 size-1 rounded-full bg-you"
          style={{ left: `${x}%`, animationDelay: `${delay}s` }}
        />
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- people */

function NightPerson({ placed }: { placed: Placed }) {
  const { person, left, top, depth, scale } = placed;

  return (
    <div
      data-faded={person.faded || undefined}
      className="group absolute flex -translate-x-1/2 -translate-y-full flex-col items-center gap-1"
      style={{ left, top, zIndex: 1 + Math.round(depth * 20) }}
    >
      {person.message ? (
        <span className="max-w-24 rounded-2xl rounded-bl-sm border border-border bg-popover/95 px-2 py-1 text-center text-xs leading-tight text-foreground shadow-sm backdrop-blur-xl sm:max-w-40 sm:px-2.5 sm:py-1.5">
          {person.message}
        </span>
      ) : null}

      <div className="relative grid place-items-end group-data-faded:opacity-45">
        {person.speaking ? (
          <span
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-4 rounded-[50%] bg-[radial-gradient(ellipse,hsl(var(--primary)/0.6),transparent_70%)]"
          />
        ) : null}
        {person.isYou ? (
          <span
            aria-hidden="true"
            className="absolute inset-x-1 bottom-0 h-3 rounded-[50%] bg-[radial-gradient(ellipse,hsl(var(--you)/0.45),transparent_70%)]"
          />
        ) : null}

        <AnimalFigure
          animal={person.beast}
          colour={person.colour}
          pose={person.speaking ? "talk" : "stand"}
          dim={person.faded}
          className="origin-bottom"
          style={{
            height: `calc(var(--fig-h) * ${scale.toFixed(3)})`,
            width: `calc(var(--fig-h) * ${(scale * 0.74).toFixed(3)})`,
          }}
        />

        {person.raised ? (
          <span className="absolute -right-2 bottom-0 grid size-5 place-items-center rounded-full bg-primary text-primary-foreground shadow-glow-sm sm:-right-3 sm:size-6">
            <Hand className="size-3 sm:size-3.5" aria-hidden="true" />
          </span>
        ) : null}
      </div>

      <span
        data-you={person.isYou || undefined}
        className="flex max-w-28 items-center gap-1 rounded-full bg-background/80 px-2 py-0.5 text-xs whitespace-nowrap text-foreground backdrop-blur-sm group-data-faded:text-muted-foreground data-you:text-you"
      >
        {person.muted ? (
          <MicOff className="size-3.5 flex-none text-stop" aria-hidden="true" />
        ) : null}
        <span className="truncate">{person.name}</span>
        {person.note ? (
          <span className="flex-none text-muted-foreground">
            · {person.note}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/** The lit centre of the room: the night clearing around the fire. */
export function NightStage({
  people,
  mode = "fire",
  className,
}: NightStageProps) {
  const placed = placePeople(people);

  return (
    <div
      data-mode={mode}
      className={cn(
        "relative isolate min-h-[min(340px,45dvh)] flex-1 overflow-hidden rounded-2xl border border-raise bg-background",
        "[--fig-h:88px] [--ring-cy:67%] [--ring-rx:30%] [--ring-ry:27%]",
        "sm:[--fig-h:108px] sm:[--ring-rx:33%] sm:[--ring-ry:28%]",
        "lg:[--fig-h:126px]",
        className,
      )}
    >
      <NightSky />
      <PineRing />
      <Clearing lit={mode === "fire"} />

      {mode === "fire" ? (
        <NightFire />
      ) : (
        <span
          aria-hidden="true"
          className="absolute left-1/2 aspect-[2/1] w-[74%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-dashed border-primary/45 bg-[radial-gradient(ellipse,hsl(var(--primary)/0.12),transparent_70%)]"
          style={{ top: "var(--ring-cy)" }}
        />
      )}

      {placed.map((item) => (
        <NightPerson key={item.person.id} placed={item} />
      ))}

      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,transparent_45%,hsl(var(--background)/0.75))]"
      />

      {placed.map(({ person, left, top, scale }) =>
        person.reaction ? (
          <span
            key={`reaction-${person.id}`}
            className="absolute z-50 -translate-x-1/2 -translate-y-full"
            style={{
              left,
              top: `calc(${top} - 1.5rem - (var(--fig-h) * ${scale.toFixed(3)}))`,
            }}
          >
            <span className="camp-reaction grid size-8 place-items-center rounded-xl border border-border bg-popover text-base shadow-glow-sm sm:size-9 sm:text-lg">
              {person.reaction.startsWith("http") ? (
                <img
                  src={person.reaction}
                  alt=""
                  className="size-8 rounded-lg object-cover"
                />
              ) : (
                person.reaction
              )}
            </span>
          </span>
        ) : null,
      )}
    </div>
  );
}

export default NightStage;
